/**
 * Untraced fetch for step-up-guarded actions.
 *
 * Step-up requests carry `currentPassword` / `totpCode` in the body. Those must
 * NEVER enter the telemetry `apiTraces` ring buffer (surfaced by
 * `window.fiasDebug.getState()` and the report-a-problem dialog) — and
 * `sanitizeForLogging` only truncates by size, it does not redact by key. The
 * `api.ts` client traces every request/response; this helper deliberately does
 * NOT, mirroring the auth-api `authFetch` posture so credentials stay out of
 * frontend telemetry. Sends cookies + the CSRF header; throws
 * {@link StepUpFetchError} (carrying `.code`) on non-2xx so the step-up UI can
 * branch on the standard step-up error codes.
 */

import { API_BASE } from './api';
import { attachCsrfHeader, setCsrfToken } from './csrf';
import { sessionCredentials } from './embed-document';

export class StepUpFetchError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  constructor(message: string, status: number, code: string | undefined) {
    super(message);
    this.name = 'StepUpFetchError';
    this.status = status;
    this.code = code;
  }
}

export async function stepUpFetch<T>(
  path: string,
  init: { method?: string; body?: Record<string, unknown> } = {},
): Promise<T> {
  const method = init.method ?? 'POST';
  const headers: Record<string, string> = {};
  if (init.body !== undefined) headers['Content-Type'] = 'application/json';
  attachCsrfHeader(method, headers);

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    credentials: sessionCredentials(),
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });

  if (response.ok) {
    const data = (await response.json().catch(() => ({}))) as {
      csrfToken?: string;
    } & Record<string, unknown>;
    if (typeof data.csrfToken === 'string') setCsrfToken(data.csrfToken);
    return data as T;
  }

  let message = response.statusText;
  let code: string | undefined;
  try {
    const body = (await response.json()) as { error?: { message?: string; code?: string } };
    if (body.error?.message) message = body.error.message;
    if (body.error?.code) code = body.error.code;
  } catch {
    // Non-JSON error body — fall back to statusText.
  }
  throw new StepUpFetchError(message, response.status, code);
}
